/**
 * What every route under `/l/:slug` can read: the published lottery, the resolved theme and a refetch. Feature
 * agents call `useLottery()` inside their route components; outside a LotteryLayout it throws (programming
 * error, not a participant state).
 */
import { createContext, useContext } from 'react';
import type { PublicLotteryDto } from '@windrop/contracts';
import type { AppliedTheme } from '../lib/theme';

export interface LotteryContextValue {
  slug: string;
  lottery: PublicLotteryDto;
  theme: AppliedTheme | null;
  /** Re-fetch `GET /v1/l/:slug` (countdown zero, status change). */
  refetch: () => Promise<unknown>;
  /** True while a background refetch is in flight. */
  refreshing: boolean;
  /** When the payload came from cache after a failed refetch (Appendix A `stale_data`). */
  staleSince: string | null;
}

export const LotteryContext = createContext<LotteryContextValue | null>(null);

export function useLottery(): LotteryContextValue {
  const ctx = useContext(LotteryContext);
  if (!ctx) throw new Error('useLottery() must be used under LotteryLayout (/l/:slug routes)');
  return ctx;
}

export function useOptionalLottery(): LotteryContextValue | null {
  return useContext(LotteryContext);
}
